'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

import { TabSwitcher } from '@/components';
import { RelatedTab } from '@/types';

interface RelatedTabsSwitcherProps {
  relatedTabs: RelatedTab[];
}

/**
 * RelatedTabsSwitcher - Component for switching between related sports tabs/markets.
 * Maps RelatedTab data from API to TabSwitcher options and handles navigation.
 */
export const RelatedTabsSwitcher: React.FC<RelatedTabsSwitcherProps> = ({ relatedTabs }) => {
  const router = useRouter();

  // Find the currently active tab based on backend-provided flag
  const activeTab = relatedTabs.find((t) => t.isCurrentTab);

  const options = relatedTabs.map((tab) => ({
    value: tab.slug,
    label: tab.label,
  }));

  const handleTabChange = (slug: string) => {
    const tab = relatedTabs.find((t) => t.slug === slug);
    if (tab && tab.eventId) {
      // Navigate to the related event's detail page
      router.push(`/sports/live/${tab.eventId}`);
    }
  };

  if (!relatedTabs || relatedTabs.length <= 1) return null;

  return (
    <TabSwitcher
      activeTab={activeTab?.slug || ''}
      onTabChange={handleTabChange}
      options={options}
      variant="subtle-pills"
      containerSx={{ mb: 1, gap: 1 }}
    />
  );
};

export default RelatedTabsSwitcher;
